import * as Sentry from '@sentry/react';
import { useMemo } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router';

import { routes } from '~/components/Router/routes';
import { EdsLinkNavigationProvider } from '~/features/app/EdsLinkNavigationProvider';
import { useClientSlug } from '~/routing/clientSlug';

import AppContents from './AppContents/AppContents';

// Pathless layout route that mounts the EDS link-navigation provider inside the
// router context (so it can access `navigate`) while wrapping every routed
// page. EDS 2.0 links (Button `href`, Link, Breadcrumb) then route client-side.
function EdsLinkNavigationLayout() {
  return (
    <EdsLinkNavigationProvider>
      <Outlet />
    </EdsLinkNavigationProvider>
  );
}

// Wrap `createBrowserRouter` so the Sentry React Router v7 browser-tracing
// integration (wired up in `src/index.tsx`) can name navigation transactions
// after the matched route template (e.g. `/documents/:documentId`) instead of
// the raw URL. Must be called after `Sentry.init`, which runs at module load
// in `src/index.tsx` before this file evaluates.
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

export function AppDataRouter() {
  // Rebuilding the router is what lets the app cross from a clientless URL
  // (`/login`) to a tenant-scoped one (`/nermal/login`) without a page load:
  // the new instance reads the already-rewritten URL against the new basename.
  const clientSlug = useClientSlug();
  const router = useMemo(
    () =>
      sentryCreateBrowserRouter(
        [
          {
            element: <AppContents />,
            children: [
              { element: <EdsLinkNavigationLayout />, children: routes },
            ],
          },
        ],
        { basename: `/${clientSlug}` },
      ),
    [clientSlug],
  );

  // Keyed so a new basename remounts the provider: `RouterProvider` subscribes
  // to the router it first mounted with and never initializes a replacement,
  // which would leave the tree rendering nothing.
  return <RouterProvider key={clientSlug} router={router} />;
}
